import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { getContest, getLeaderboard } from "../lib/api";
import { Trophy, Users, Clock, ChevronRight, Medal, Flame, Crown, Sparkles } from "lucide-react";

export default function ContestLobby() {
  const { id } = useParams();
  const [contest, setContest] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    Promise.all([getContest(id), getLeaderboard(id)]).then(([c, b]) => {
      setContest(c);
      setBoard(b);
    });
  }, [id]);

  if (!contest) {
    return (
      <div className="min-h-screen pb-safe" data-testid="contest-lobby-loading">
        <Header title="Contest" />
        <div className="max-w-xl mx-auto px-4 pt-6 text-white/60">Loading…</div>
        <BottomNav />
      </div>
    );
  }

  const end = new Date(contest.end_date);
  const ms = end - new Date();
  const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  const hours = Math.max(0, Math.floor((ms / (1000 * 60 * 60)) % 24));
  const isSettled = contest.status === "settled";
  const winners = contest.winners || [];
  const top10Lb = board.slice(0, 10);
  // Merge XP awards from winners[] by rank
  const xpByRank = Object.fromEntries(winners.map(w => [w.rank, w.xp_awarded]));
  const preview = contest.payout_preview || {};
  const entry = contest.entry_fee || 0;
  const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

  return (
    <div className="min-h-screen pb-safe" data-testid="contest-lobby">
      <Header title="Contest" />
      <div className="max-w-xl mx-auto">
        <div className="relative h-60 overflow-hidden">
          <img src={contest.image_url} alt={contest.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,8,0.2), rgba(5,5,8,0.96))" }} />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2">
              <span className="chip" style={{ background: isSettled ? "#FFD60022" : contest.status === "live" ? "#00FF6622" : "#B026FF22", color: isSettled ? "#FFD600" : contest.status === "live" ? "#00FF66" : "#B026FF", border: `1px solid ${isSettled ? "#FFD60055" : contest.status === "live" ? "#00FF6655" : "#B026FF55"}` }}>
                {isSettled ? <><Trophy className="w-3 h-3" /> Settled</> : contest.status === "live" ? "● Live" : contest.status}
              </span>
              <span className="chip" style={{ background: "#00F0FF15", color: "#00F0FF", border: "1px solid #00F0FF44" }}>
                {contest.mode === "standard" ? contest.category : contest.mode}
              </span>
            </div>
            <h1 className="font-display text-3xl font-bold mt-2 tracking-tight">{contest.name}</h1>
            <p className="text-white/70 text-sm">{contest.tagline}</p>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-4">
          {/* Entry vs Earn hero — the headline pitch */}
          <div className="glass-strong rounded-3xl p-5 relative overflow-hidden" data-testid="contest-entry-vs-earn">
            <div aria-hidden className="pointer-events-none absolute -top-12 -right-10 w-44 h-44 rounded-full opacity-30"
              style={{ background: "radial-gradient(circle,#00FF66,transparent 70%)" }} />
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/55">Build your Influencer Circle. Win Fanpuff Credits.</p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="rounded-2xl p-3" style={{ background: "rgba(255,214,0,0.08)", border: "1px solid rgba(255,214,0,0.35)" }}>
                <p className="text-[10px] tracking-widest uppercase text-[#FFD600]/85 font-bold">Entry fee</p>
                <p className="font-display text-2xl font-bold text-[#FFD600] mt-0.5">{entry === 0 ? "Free" : fmt(entry)}</p>
                <p className="text-[10px] text-white/55">Fanpuff Credits</p>
              </div>
              <div className="rounded-2xl p-3" style={{ background: "rgba(0,255,102,0.1)", border: "1px solid rgba(0,255,102,0.35)" }}>
                <p className="text-[10px] tracking-widest uppercase text-[#00FF66]/90 font-bold">#1 wins up to</p>
                <p className="font-display text-2xl font-bold text-[#00FF66] mt-0.5">{fmt(preview.top1_credits)}</p>
                <p className="text-[10px] text-white/55">Top 3 share ≈ {fmt(preview.top3_credits)}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div className="rounded-xl py-1.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <p className="text-[9px] uppercase tracking-widest text-white/50">Prize pool</p>
                <p className="font-mono-brand text-xs font-bold mt-0.5">{fmt(preview.projected_prize_pool)}</p>
              </div>
              <div className="rounded-xl py-1.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <p className="text-[9px] uppercase tracking-widest text-white/50">Paid ranks</p>
                <p className="font-mono-brand text-xs font-bold mt-0.5">Top {preview.payout_zone_size || 20}</p>
              </div>
              <div className="rounded-xl py-1.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <p className="text-[9px] uppercase tracking-widest text-white/50">Bottom risk</p>
                <p className="font-mono-brand text-xs font-bold mt-0.5 text-[#FF0055]">−{fmt(preview.bottom_penalty_max || 8000)}</p>
              </div>
            </div>
            <p className="text-[10px] text-white/40 mt-3 leading-relaxed">
              70% of total entries return as prizes (top-heavy). 30% sinks (anti-inflation). Bottom 10% lose a small skill-based credit penalty.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <StatBox icon={Users} label="Entries" value={contest.current_entries?.toLocaleString("en-IN")} />
            <StatBox icon={Trophy} label="Entry" value={contest.entry_fee === 0 ? "Free" : `${fmt(contest.entry_fee)}`} />
            <StatBox icon={Clock} label="Ends" value={`${days}d ${hours}h`} />
          </div>

          <div className="glass rounded-2xl p-4">
            <h3 className="font-display text-lg font-semibold">About</h3>
            <p className="text-sm text-white/70 mt-1">{contest.description}</p>
          </div>

          <div className="glass rounded-2xl p-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Medal className="w-4 h-4 text-[#FFD600]" /> Win credits → unlock real rewards
            </h3>
            <p className="text-[11px] text-white/55 mt-1">
              Every contest credit you win compounds toward your Reward Vault tier. Vouchers · earbuds · smartwatches · iPhone · Smart TV.
            </p>
            <ul className="mt-2 space-y-2 text-sm text-white/80">
              {contest.prizes.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: "#B026FF" }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-white/40 mt-3">{contest.prize_pool_description}</p>
          </div>

          {!isSettled && (
            <Link
              to={`/contests/${contest.contest_id}/build`}
              data-testid="join-contest-cta"
              className="block w-full text-center py-3.5 rounded-full font-bold text-black"
              style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)", boxShadow: "0 0 22px rgba(176,38,255,0.35)" }}
            >
              Build your Circle · Win up to {fmt(preview.top1_credits)} →
            </Link>
          )}

          {isSettled && (
            <div className="glass-strong rounded-2xl p-4 relative overflow-hidden" data-testid="winners-podium">
              <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-40" style={{ background: "radial-gradient(circle,#FFD600,transparent 70%)" }} />
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FFD600]" />
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FFD600]">Contest Settled</p>
              </div>
              <h3 className="font-display text-xl font-bold mt-1">Top 10 XP bonuses awarded</h3>
              <p className="text-xs text-white/60">
                {top10Lb.length ? `${top10Lb.length} lineups paid out` : "No lineups participated"}
              </p>
              {top10Lb.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {top10Lb.slice(0, 3).map((row, i) => {
                    const color = ["#FFD600", "#C0C0C0", "#CD7F32"][i];
                    return (
                      <div key={row.lineup_id} className="rounded-2xl p-3 text-center" style={{ background: `${color}18`, border: `1px solid ${color}55` }} data-testid={`podium-${i + 1}`}>
                        <Crown className="w-4 h-4 mx-auto" style={{ color }} />
                        <p className="text-[10px] uppercase tracking-widest font-bold mt-1" style={{ color }}>#{row.rank}</p>
                        <p className="font-display text-sm font-bold mt-0.5 truncate">{row.name}</p>
                        <p className="text-[11px] text-white/60 font-mono-brand mt-0.5">{row.points.toFixed(0)} pts</p>
                        {xpByRank[row.rank] != null && (
                          <p className="text-[10px] font-bold mt-1" style={{ color }}>+{xpByRank[row.rank].toLocaleString("en-IN")} XP</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="font-display text-lg font-semibold mt-3 mb-2 flex items-center gap-2">
              <Flame className="w-4 h-4 text-[#FF0055]" /> {isSettled ? "Final Leaderboard" : "Leaderboard"}
            </h3>
            <div className="glass rounded-2xl divide-y divide-white/5">
              {board.length === 0 && <p className="p-4 text-sm text-white/50">Be the first to enter!</p>}
              {board.map((row) => (
                <div key={row.lineup_id} className="flex items-center gap-3 p-3" data-testid={`lb-row-${row.rank}`}>
                  <span className="w-7 text-center font-mono-brand text-sm text-white/60">#{row.rank}</span>
                  {row.avatar ? (
                    <img src={row.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-white/10" />
                  )}
                  <span className="flex-1 text-sm truncate">{row.name}</span>
                  {isSettled && xpByRank[row.rank] != null && (
                    <span className="chip" style={{ background: "#FFD60022", color: "#FFD600", border: "1px solid #FFD60055", fontSize: 10 }}>+{xpByRank[row.rank]} XP</span>
                  )}
                  <span className="font-mono-brand text-sm neon-up">{row.points.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="h-10" />
      </div>
      <BottomNav />
    </div>
  );
}

function StatBox({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-2xl p-3">
      <div className="flex items-center gap-1 text-[10px] tracking-widest uppercase text-white/50">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className="font-display text-lg font-semibold mt-1">{value}</p>
    </div>
  );
}
